import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Home, HardHat } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Field } from "@/components/site/Common";
import { AuthAside, useAuthSuccess } from "./Login";
import { TRADES, CITIES } from "@/lib/hz";
import { post, errMsg } from "@/lib/api";

export default function Register() {
  const [sp] = useSearchParams();
  const [role, setRole] = useState(sp.get("role") === "profesional" ? "profesional" : "cliente");
  const [f, setF] = useState({ name: "", email: "", password: "", phone: "", city: "Madrid", nif: "", address: "", bio: "", hourly_rate: "" });
  const [trades, setTrades] = useState([]);
  const [busy, setBusy] = useState(false);
  const ok = useAuthSuccess();
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const isPro = role === "profesional";
  const toggle = (id) => setTrades(trades.includes(id) ? trades.filter((t) => t !== id) : [...trades, id]);

  const submit = async (e) => {
    e.preventDefault();
    if (isPro && !trades.length) return toast.error("Elige al menos un oficio");
    setBusy(true);
    try {
      ok(await post("/auth/register", { ...f, role, trades: isPro ? trades : [], hourly_rate: isPro && f.hourly_rate ? Number(f.hourly_rate) : null }));
    } catch (err) { toast.error(errMsg(err, "No se pudo crear la cuenta")); }
    finally { setBusy(false); }
  };

  const roleBtn = (v, Icon, title, text) => (
    <button type="button" data-testid={`register-role-${v}`} onClick={() => setRole(v)}
      className={`flex flex-1 items-start gap-3 rounded-2xl border-2 p-4 text-left transition-colors ${role === v ? "border-orange-500 bg-orange-50" : "border-slate-200 hover:border-slate-300"}`}>
      <Icon className={`mt-0.5 size-5 ${role === v ? "text-orange-600" : "text-slate-400"}`} />
      <span><span className="block font-semibold">{title}</span><span className="text-xs text-slate-500">{text}</span></span>
    </button>
  );

  return (
    <div className="mx-auto grid max-w-6xl gap-8 px-4 py-12 lg:grid-cols-2">
      <AuthAside />
      <div className="rounded-3xl border border-slate-200 bg-white p-7 shadow-sm md:p-10">
        <h1 className="font-heading text-3xl font-extrabold">Crear cuenta</h1>
        <p className="mt-1 text-sm text-slate-500">Podrás usarla también en la app HandyZoom.</p>
        <div className="mt-6 flex flex-col gap-3 sm:flex-row">
          {roleBtn("cliente", Home, "Necesito servicios", "Pide presupuestos y contrata")}
          {roleBtn("profesional", HardHat, "Soy profesional", "Recibe trabajos y factura")}
        </div>
        <form onSubmit={submit} className="mt-6 space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Nombre completo"><Input required value={f.name} onChange={set("name")} className="h-11" data-testid="register-name-input" /></Field>
            <Field label="Teléfono"><Input value={f.phone} onChange={set("phone")} className="h-11" data-testid="register-phone-input" /></Field>
            <Field label="Email"><Input required type="email" value={f.email} onChange={set("email")} className="h-11" data-testid="register-email-input" /></Field>
            <Field label="Contraseña"><Input required type="password" minLength={6} value={f.password} onChange={set("password")} className="h-11" data-testid="register-password-input" /></Field>
            <Field label="Ciudad">
              <Select value={f.city} onValueChange={(v) => setF({ ...f, city: v })}>
                <SelectTrigger className="h-11" data-testid="register-city-select"><SelectValue /></SelectTrigger>
                <SelectContent>{CITIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="NIF / NIE (para facturas)"><Input value={f.nif} onChange={set("nif")} className="h-11" data-testid="register-nif-input" /></Field>
          </div>
          <Field label="Dirección"><Input value={f.address} onChange={set("address")} className="h-11" data-testid="register-address-input" /></Field>
          {isPro && (
            <>
              <Field label="Tus oficios">
                <div className="flex flex-wrap gap-2">
                  {TRADES.map((t) => (
                    <button key={t.id} type="button" onClick={() => toggle(t.id)} data-testid={`register-trade-${t.id}`}
                      className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-sm font-semibold ${trades.includes(t.id) ? "border-slate-900 bg-slate-900 text-white" : "border-slate-200 text-slate-700"}`}><t.icon className="size-4" />{t.name}</button>
                  ))}
                </div>
              </Field>
              <div className="grid gap-4 sm:grid-cols-[1fr_2fr]">
                <Field label="Tarifa €/hora"><Input type="number" min="0" value={f.hourly_rate} onChange={set("hourly_rate")} className="h-11" data-testid="register-rate-input" /></Field>
                <Field label="Presentación"><Textarea rows={2} value={f.bio} onChange={set("bio")} data-testid="register-bio-input" placeholder="Experiencia, especialidades…" /></Field>
              </div>
            </>
          )}
          <Button type="submit" size="lg" className="w-full rounded-xl" disabled={busy} data-testid="register-submit-btn">{busy && <Loader2 className="animate-spin" />}Crear cuenta</Button>
        </form>
        <p className="mt-6 text-center text-sm text-slate-600">¿Ya tienes cuenta? <Link to="/login" className="font-semibold text-orange-600">Entrar</Link></p>
      </div>
    </div>
  );
}
